import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { timeout } from './timeout';

export class SplineSceneController {
  constructor(options = {}) {
    const {
      parent,
      canvas,
      poster = null,
      blob = null,
      sceneURL,

      // ScrollTrigger
      start = 'top top',
      end = 'bottom+=100px top',

      // Visuals
      fadeDuration = 0.2,
      fadeDelayOnEnter = 0.25,

      // LocalStorage-based load postponement
      localStorageKey = 'splineLoaded', // base key; will be made unique per page
      useFirstLoadDelays = true,
      firstLoadDelayMs = 3000,
      repeatLoadDelayMs = 100,

      // Takes first frame screenshot for the poster
      takeScreenshot = true,

      // Optional hook
      onLoaded = null,
    } = options;

    this.parent = parent;
    this.canvas = canvas;
    this.poster = poster;
    this.blob = blob;
    this.sceneUrl = sceneURL;

    this.start = start;
    this.end = end;
    this.fadeDuration = fadeDuration;
    this.fadeDelayOnEnter = fadeDelayOnEnter;

    // Page-scoped localStorage key
    this.localStorageKeyBase = localStorageKey;
    this.localStorageKey = this._computePageScopedKey(localStorageKey);

    this.useFirstLoadDelays = useFirstLoadDelays;
    this.firstLoadDelayMs = firstLoadDelayMs;
    this.repeatLoadDelayMs = repeatLoadDelayMs;

    this.takeScreenshot = takeScreenshot;
    this.onLoaded = onLoaded;

    this.spline = null;
    this.trigger = null;
  }

  _normalizePath(pathname) {
    if (!pathname) return '/';
    if (pathname.length > 1 && pathname.endsWith('/')) {
      return pathname.slice(0, -1);
    }
    return pathname;
  }

  _computePageScopedKey(baseKey) {
    try {
      const path = this._normalizePath(
        window.location && window.location.pathname,
      );
      // Use pathname to scope per page (query/hash ignored)
      return `${baseKey}::${encodeURIComponent(path || '/')}`;
    } catch {
      // Fallback if window is unavailable
      return baseKey;
    }
  }

  async init() {
    if (!this.parent || !this.canvas || !this.sceneUrl) return null;

    // LocalStorage-based postponement (page-scoped)
    if (this.useFirstLoadDelays) {
      const isFirstLoad = localStorage.getItem(this.localStorageKey) !== 'true';
      if (isFirstLoad) {
        await timeout(this.firstLoadDelayMs);
        localStorage.setItem(this.localStorageKey, 'true');
      } else {
        await timeout(this.repeatLoadDelayMs);
      }
    }

    if (this.takeScreenshot) {
      this.canvas.getContext('webgl2', {
        preserveDrawingBuffer: true,
      });
    }

    await this._loadScene();
    this.enableHeroBlobDrift();

    if (this.takeScreenshot) {
      const img = new Image();
      img.src = this.canvas.toDataURL();
      document.body.appendChild(img);
    }

    this.trigger = ScrollTrigger.create({
      trigger: this.parent,
      start: this.start,
      end: this.end,
      onLeave: () => {
        gsap.to(this.canvas, { opacity: 0, duration: this.fadeDuration });
        this.dispose();
        this.canvas.removeAttribute('style');
      },
      onEnterBack: async () => {
        await this._loadScene();
        gsap.to(this.canvas, {
          opacity: 1,
          duration: this.fadeDuration,
          delay: this.fadeDelayOnEnter,
        });
      },
    });

    return this;
  }

  enableHeroBlobDrift(options = {}) {
    const container = this.parent;

    // Ensure centered on init (explicitly set vars to 0)
    container.style.setProperty('--blob-offset-x', '0px');
    container.style.setProperty('--blob-offset-y', '0px');

    const computed = getComputedStyle(container);
    const cssRange = parseFloat(computed.getPropertyValue('--blob-range')) || 0;
    const maxShiftDefault =
      cssRange ||
      Math.round(
        Math.min(container.clientWidth, container.clientHeight) * 0.15,
      );
    const maxShift = options.maxShift ?? maxShiftDefault;

    let raf = null;
    let targetX = 0,
      targetY = 0;

    const setOffsets = () => {
      raf = null;
      container.style.setProperty('--blob-offset-x', `${targetX}px`);
      container.style.setProperty('--blob-offset-y', `${targetY}px`);
    };

    const schedule = () => {
      if (raf == null) raf = requestAnimationFrame(setOffsets);
    };

    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const relX = (e.clientX - rect.left) / rect.width; // 0..1
      const relY = (e.clientY - rect.top) / rect.height; // 0..1

      // Normalize to -1..1 where 0 is center
      const nx = Math.max(-1, Math.min(1, (relX - 0.5) * 2));
      const ny = Math.max(-1, Math.min(1, (relY - 0.5) * 2));

      // Move opposite to pointer
      targetX = -nx * maxShift;
      targetY = -ny * maxShift;

      schedule();
    };

    const onLeave = () => {
      targetX = 0;
      targetY = 0;
      schedule();
    };

    container.addEventListener('pointermove', onMove);
    container.addEventListener('pointerleave', onLeave);

    return () => {
      container.removeEventListener('pointermove', onMove);
      container.removeEventListener('pointerleave', onLeave);
    };
  }

  async _loadScene() {
    if (this.spline) return;
    const { Application } = await import('@splinetool/runtime');
    this.spline = new Application(this.canvas);
    await this.spline.load(this.sceneUrl);
    await timeout(100);
    if (this.poster) this.poster.remove();

    if (typeof this.onLoaded === 'function') await this.onLoaded();
  }

  getSpline() {
    return this.spline;
  }

  async reload() {
    this.dispose();
    await this._loadScene();
  }

  dispose() {
    if (this.spline) {
      try {
        this.spline.dispose();
      } catch {}
      this.spline = null;
    }
  }

  destroy() {
    if (this.trigger) {
      this.trigger.kill();
      this.trigger = null;
    }
    this.dispose();
  }
}
